export const ICE_DISCONNECT_GRACE_MS = 15_000;

export const ICE_RECONNECTING_LABEL = "Reconnecting…";

export type IceSignal = "ice" | "connection";

export type IceMark = "idle" | "up" | "lost" | "down";

export type IceLink = {
  seenUp: boolean;
  ice: IceMark;
  connection: IceMark;
  phase: "starting" | "up" | "reconnecting" | "ended";
};

export const initialIceLink: IceLink = {
  seenUp: false,
  ice: "idle",
  connection: "idle",
  phase: "starting",
};

export type IceLinkEvent =
  | { type: "state"; signal: IceSignal; state: string }
  | { type: "grace" };

export type IceEffect =
  | { type: "arm-grace" }
  | { type: "clear-grace" }
  | { type: "show-reconnecting" }
  | { type: "hide-reconnecting" }
  | { type: "ice-fail" };

export function classifyIceState(state: string): "up" | "lost" | "down" | "ignore" {
  if (state === "connected" || state === "completed") return "up";
  if (state === "disconnected") return "lost";
  if (state === "failed") return "down";
  return "ignore";
}

export function reduceIceLink(link: IceLink, event: IceLinkEvent): { link: IceLink; effects: IceEffect[] } {
  if (link.phase === "ended") return { link, effects: [] };
  if (event.type === "grace") {
    if (link.phase !== "reconnecting") return { link, effects: [] };
    return { link: { ...link, phase: "ended" }, effects: [{ type: "ice-fail" }] };
  }
  const mark = classifyIceState(event.state);
  if (mark === "ignore") return { link, effects: [] };
  const next: IceLink = { ...link, [event.signal]: mark };
  if (mark === "up") next.seenUp = true;
  if (next.ice === "down" || next.connection === "down") {
    // TODO: restartIce once and renegotiate the current SignalR offer before ice-fail.
    // A second sdp-offer re-arms the agent offer watch, which clears a settled session and can end it.
    const effects: IceEffect[] = [];
    if (link.phase === "reconnecting") effects.push({ type: "clear-grace" });
    effects.push({ type: "ice-fail" });
    return { link: { ...next, phase: "ended" }, effects };
  }
  const lost = next.ice === "lost" || next.connection === "lost";
  if (lost && next.seenUp) {
    if (link.phase === "reconnecting") return { link: { ...next, phase: "reconnecting" }, effects: [] };
    return {
      link: { ...next, phase: "reconnecting" },
      effects: [{ type: "arm-grace" }, { type: "show-reconnecting" }],
    };
  }
  if (link.phase === "reconnecting") {
    return {
      link: { ...next, phase: "up" },
      effects: [{ type: "clear-grace" }, { type: "hide-reconnecting" }],
    };
  }
  return { link: { ...next, phase: next.seenUp ? "up" : "starting" }, effects: [] };
}
